import React, { useEffect } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Alert from '@mui/joy/Alert';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import {
  X as CloseIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  XCircle as ErrorIcon,
  Envelope as EmailIcon,
  ChatCircle as SmsIcon,
  Phone as PhoneIcon,
  ClipboardText as TaskIcon,
  Calendar as EventIcon,
  EnvelopeOpen as MarkEmailReadIcon,
} from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';

export interface Toast {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message?: string;
  icon?: React.ReactNode;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastNotificationsProps {
  toasts: Toast[];
  onRemoveToast: (id: string) => void;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
}

const getIcon = (type: Toast['type'], customIcon?: React.ReactNode) => {
  if (customIcon) return customIcon;
  
  switch (type) {
    case 'info':
      return <InfoIcon />;
    case 'success':
      return <CheckCircleIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'error':
      return <ErrorIcon />;
    default:
      return <InfoIcon />;
  }
};

const getPositionStyles = (position: string) => {
  const base = {
    position: 'fixed' as const,
    zIndex: 9999,
    pointerEvents: 'none' as const,
  };

  switch (position) {
    case 'top-left':
      return { ...base, top: 20, left: 20 };
    case 'top-center':
      return { ...base, top: 20, left: '50%', transform: 'translateX(-50%)' };
    case 'top-right':
      return { ...base, top: 20, right: 20 };
    case 'bottom-left':
      return { ...base, bottom: 20, left: 20 };
    case 'bottom-center':
      return { ...base, bottom: 20, left: '50%', transform: 'translateX(-50%)' };
    case 'bottom-right':
      return { ...base, bottom: 20, right: 20 };
    default:
      return { ...base, top: 20, right: 20 };
  }
};

const ToastItem: React.FC<{ toast: Toast; onRemove: () => void }> = ({ toast, onRemove }) => {
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(onRemove, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, onRemove]);

  const alertColor = toast.type === 'error' ? 'danger' : 
                     toast.type === 'warning' ? 'warning' : 
                     toast.type === 'success' ? 'success' : 'primary';

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.9 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      style={{ pointerEvents: 'auto' }}
    >
      <Alert
        color={alertColor}
        variant="soft"
        startDecorator={getIcon(toast.type, toast.icon)}
        endDecorator={
          <IconButton
            variant="plain"
            size="sm"
            color={alertColor}
            onClick={onRemove}
          >
            <CloseIcon />
          </IconButton>
        }
        sx={{
          minWidth: 300,
          maxWidth: 400,
          boxShadow: 'lg',
          mb: 2,
        }}
      >
        <Stack spacing={0.5}>
          <Typography level="title-sm" fontWeight="bold">
            {toast.title}
          </Typography>
          {toast.message && (
            <Typography level="body-sm">
              {toast.message}
            </Typography>
          )}
          {toast.action && (
            <Box sx={{ mt: 1 }}>
              <Typography
                level="body-sm"
                sx={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 },
                }}
                onClick={toast.action.onClick}
              >
                {toast.action.label}
              </Typography>
            </Box>
          )}
        </Stack>
      </Alert>
    </motion.div>
  );
};

const ToastNotifications: React.FC<ToastNotificationsProps> = ({
  toasts,
  onRemoveToast,
  position = 'top-right',
}) => {
  return (
    <Box sx={getPositionStyles(position)}>
      <AnimatePresence mode="sync">
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onRemove={() => onRemoveToast(toast.id)}
          />
        ))}
      </AnimatePresence>
    </Box>
  );
};

// Helper function to create inbox-specific toasts
export const createInboxToast = (
  type: 'new_email' | 'new_sms' | 'new_call' | 'new_task' | 'new_meeting' | 'email_opened',
  data: any
): Toast => {
  const baseToast: Partial<Toast> = {
    id: `${type}-${Date.now()}`,
    type: 'info',
    duration: 5000,
  };

  switch (type) {
    case 'new_email':
      return {
        ...baseToast,
        title: 'New Email',
        message: `From: ${data.from || 'Unknown'}`,
        icon: <EmailIcon />,
        action: data.action,
      } as Toast;

    case 'new_sms':
      return {
        ...baseToast,
        title: 'New SMS',
        message: `From: ${data.from || 'Unknown'}`,
        icon: <SmsIcon />,
        action: data.action,
      } as Toast;

    case 'new_call':
      return {
        ...baseToast,
        title: 'New Call',
        message: `From: ${data.from || 'Unknown'}`,
        icon: <PhoneIcon />,
        action: data.action,
      } as Toast;

    case 'new_task':
      return {
        ...baseToast,
        title: 'New Task',
        message: data.title,
        icon: <TaskIcon />,
        action: data.action,
      } as Toast;

    case 'new_meeting':
      return {
        ...baseToast,
        title: 'New Meeting',
        message: data.title,
        icon: <EventIcon />,
        action: data.action,
      } as Toast;

    case 'email_opened':
      return {
        ...baseToast,
        title: 'Email Opened',
        message: `${data.recipient} opened your email`,
        icon: <MarkEmailReadIcon />,
        type: 'success',
        duration: 3000,
      } as Toast;

    default:
      return {
        ...baseToast,
        title: 'Notification',
        message: data.message,
      } as Toast;
  }
};

export default ToastNotifications;